// src/views/AdminCreateTodo.jsx
import React, {useState, useEffect} from "react";
import {Container, Breadcrumb} from "react-bootstrap";
import {Link, useNavigate} from "react-router-dom";
import {
    Form,
    Input,
    DatePicker,
    TimePicker,
    Button,
    Select,
    message,
    Space,
    Card,
    Typography,
    Avatar,
    Row,
    Col,
    Tag,
    Empty,
    Calendar,
    Badge,
    List,
    Modal,
    Popconfirm,
    Divider,
    Tooltip,
    Spin,
} from "antd";
import {
    HomeOutlined,
    PlusOutlined,
    DeleteOutlined,
    UserOutlined,
    SaveOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    HolderOutlined,
    EditOutlined,
    ClockCircleOutlined,
    FieldTimeOutlined,
    InfoCircleOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import {createDailyTodo, getAllDailyTodos, deleteDailyTodo, updateDailyTodo} from "@/api/dailyTodoApi";
import {getUsersWithStatistics} from "@/api/adminApi";
import dayjs from "dayjs";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

const {Title, Text} = Typography;

// Sortable Item Component
const SortableTaskItem = ({id, index, task, onRemove, onTaskChange, users}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id: id.toString()});

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        marginBottom: '16px',
    };

    return (
        <div ref={setNodeRef} style={style}>
            <Card
                size="small"
                style={{
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    cursor: isDragging ? 'grabbing' : 'default',
                }}
                title={
                    <Space>
                        <div {...attributes} {...listeners} style={{cursor: 'grab', display: 'flex', alignItems: 'center'}}>
                            <HolderOutlined style={{fontSize: '18px', color: '#8c8c8c'}}/>
                        </div>
                        <Tag color="blue">#{index + 1}</Tag>
                        <Text strong>Vazifa {index + 1}</Text>
                    </Space>
                }
                extra={
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined/>}
                        onClick={() => onRemove()}
                        size="small"
                    >
                        O'chirish
                    </Button>
                }
            >
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    {/* Title */}
                    <div>
                        <Text type="secondary" style={{fontSize: '13px'}}>Vazifa nomi *</Text>
                        <Input
                            size="middle"
                            placeholder="Vazifa nomini kiriting"
                            value={task.title}
                            onChange={(e) => onTaskChange({...task, title: e.target.value})}
                            style={{marginTop: '4px'}}
                        />
                    </div>

                    {/* Time - Optional */}
                    <div>
                        <Text type="secondary" style={{fontSize: '13px'}}>Vaqt (ixtiyoriy)</Text>
                        <TimePicker
                            size="middle"
                            format="HH:mm"
                            placeholder="Vaqtni tanlang"
                            value={task.time}
                            onChange={(time) => onTaskChange({...task, time})}
                            style={{width: '100%', marginTop: '4px'}}
                        />
                    </div>

                    {/* Responsible Users - Optional */}
                    <div>
                        <Text type="secondary" style={{fontSize: '13px'}}>Ma'sul shaxslar (ixtiyoriy)</Text>
                        <Select
                            mode="multiple"
                            size="middle"
                            placeholder="Ma'sul shaxslarni tanlang"
                            value={task.responsible_user_ids || []}
                            onChange={(ids) => onTaskChange({...task, responsible_user_ids: ids})}
                            style={{width: '100%', marginTop: '4px'}}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={users.map((user) => ({
                                value: user.id,
                                label: user.name,
                            }))}
                        />
                    </div>

                    {/* Note - Optional */}
                    <div>
                        <Text type="secondary" style={{fontSize: '13px'}}>Izoh (ixtiyoriy)</Text>
                        <Input.TextArea
                            rows={2}
                            placeholder="Qo'shimcha izoh yozing"
                            value={task.note}
                            onChange={(e) => onTaskChange({...task, note: e.target.value})}
                            style={{marginTop: '4px'}}
                        />
                    </div>
                </Space>
            </Card>
        </div>
    );
};

const AdminCreateTodo = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedUser, setSelectedUser] = useState(null);
    const [tasks, setTasks] = useState([]);

    // Todo lists state
    const [todoLists, setTodoLists] = useState([]);
    const [loadingLists, setLoadingLists] = useState(false);
    const [filterDate, setFilterDate] = useState(dayjs());
    const [editingTodo, setEditingTodo] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchUsers();
        fetchTodoLists();
    }, [filterDate]);

    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            const data = await getUsersWithStatistics();
            setUsers(data || []);
        } catch (error) {
            console.error("Error fetching users:", error);
            message.error("Foydalanuvchilar yuklanmadi");
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchTodoLists = async () => {
        setLoadingLists(true);
        try {
            const params = {
                date_from: filterDate.format("YYYY-MM-DD"),
                date_to: filterDate.format("YYYY-MM-DD"),
            };
            const data = await getAllDailyTodos(params);
            setTodoLists(data.items || []);
        } catch (error) {
            console.error("Error fetching todo lists:", error);
            message.error("Vazifalar ro'yxati yuklanmadi");
        } finally {
            setLoadingLists(false);
        }
    };

    const handleDeleteTodo = async (todoId) => {
        try {
            await deleteDailyTodo(todoId);
            message.success("Vazifa o'chirildi");
            fetchTodoLists();
        } catch (error) {
            console.error("Error deleting todo:", error);
            message.error("Vazifani o'chirishda xatolik");
        }
    };

    const handleEditTodo = (todo) => {
        setEditingTodo(todo);
        setEditModalVisible(true);
    };

    const handleUpdateTodo = async () => {
        try {
            await updateDailyTodo(editingTodo.id, {
                date: dayjs(editingTodo.date).format("YYYY-MM-DD"),
            });
            message.success("Vazifa yangilandi");
            setEditModalVisible(false);
            setEditingTodo(null);
            fetchTodoLists();
        } catch (error) {
            console.error("Error updating todo:", error);
            message.error("Vazifani yangilashda xatolik");
        }
    };

    const handleDragEnd = (event) => {
        const {active, over} = event;

        if (active.id !== over.id) {
            setTasks((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                const newItems = arrayMove(items, oldIndex, newIndex);
                // Update order
                return newItems.map((item, idx) => ({...item, order: idx}));
            });
        }
    };

    const handleAddTask = () => {
        const newTask = {
            id: Date.now(), // Temporary ID
            title: '',
            time: null,
            note: '',
            responsible_user_ids: [],
            order: tasks.length,
        };
        setTasks([...tasks, newTask]);
    };

    const handleRemoveTask = (taskId) => {
        setTasks(tasks.filter(t => t.id !== taskId).map((t, idx) => ({...t, order: idx})));
    };

    const handleTaskChange = (taskId, updatedTask) => {
        setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
    };

    const handleSubmit = async (values) => {
        if (!tasks || tasks.length === 0) {
            message.warning("Kamida bitta vazifa qo'shing!");
            return;
        }

        const emptyTasks = tasks.filter(t => !t.title || t.title.trim() === '');
        if (emptyTasks.length > 0) {
            message.warning("Barcha vazifalar nomini to'ldiring!");
            return;
        }

        setLoading(true);
        try {
            const todoData = {
                date: values.date.format("YYYY-MM-DD"),
                assigned_to_id: values.assigned_to_id,
                items: tasks.map(t => ({
                    title: t.title,
                    time: t.time ? t.time.format("HH:mm:ss") : null,
                    note: t.note || null,
                    responsible_user_ids: t.responsible_user_ids || [],
                    order: t.order
                })),
            };

            await createDailyTodo(todoData);
            message.success("Kunlik vazifalar ro'yxati yaratildi! Ma'sul shaxslarga bildirishnoma yuborildi.");
            form.resetFields();
            setSelectedUser(null);
            setTasks([]);
            fetchTodoLists(); // Refresh the list
        } catch (error) {
            console.error("Error creating todo:", error);
            message.error(
                error.response?.data?.message || "Vazifalar ro'yxati yaratishda xatolik"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleUserChange = (userId) => {
        const user = users.find(u => u.id === userId);
        setSelectedUser(user);
    };

    return (
        <Container fluid className={'p-4'}>
            {/* Breadcrumb */}
            <Breadcrumb className="mb-4">
                <Breadcrumb.Item linkAs={Link} linkProps={{to: "/"}}>
                    <HomeOutlined style={{fontSize: "16px"}}/>
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Kunlik vazifalar yaratish</Breadcrumb.Item>
            </Breadcrumb>

            {/* Header */}
            <div style={{marginBottom: "32px"}}>

                <Text type="secondary" style={{fontSize: "16px"}}>
                </Text>
            </div>

            <Row gutter={[24, 24]}>
                {/* Left Column - Form */}
                <Col xs={24} lg={12}>
                    <Card
                        title={'Foydalanuvchi uchun sana bo\'yicha kunlik vazifalar ro\'yxatini yarating'}
                        variant="borderless"
                        style={{
                            borderRadius: "12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                        }}
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                            initialValues={{
                                date: dayjs(),
                                items: [],
                            }}
                        >
                            {/* Date & User Selection */}
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={
                                            <span style={{fontSize: "15px", fontWeight: 500}}>
                          <CalendarOutlined/> Sana
                        </span>
                                        }
                                        name="date"
                                        rules={[{required: true, message: "Sanani tanlang!"}]}
                                    >
                                        <DatePicker
                                            format="DD.MM.YYYY"
                                            size="middle"
                                            style={{width: "100%"}}
                                            placeholder="Sanani tanlang"
                                            onChange={setSelectedDate}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={
                                            <span style={{fontSize: "15px", fontWeight: 500}}>
                          <UserOutlined/> Foydalanuvchi
                        </span>
                                        }
                                        name="assigned_to_id"
                                        rules={[
                                            {required: true, message: "Foydalanuvchini tanlang!"},
                                        ]}
                                    >
                                        <Select
                                            size="middle"
                                            placeholder="Foydalanuvchini tanlang"
                                            showSearch
                                            loading={loadingUsers}
                                            onChange={handleUserChange}
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.label ?? "")
                                                    .toLowerCase()
                                                    .includes(input.toLowerCase())
                                            }
                                            options={users.map((user) => ({
                                                value: user.id,
                                                label: `${user.name}`,
                                            }))}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Selected User Info */}
                            {selectedUser && (
                                <Card
                                    size="small"
                                    style={{
                                        marginBottom: "24px",
                                        background: "#f0f5ff",
                                        border: "1px solid #d6e4ff"
                                    }}
                                >
                                    <Space>
                                        <Avatar size={40} src={selectedUser.picture} icon={<UserOutlined/>}/>
                                        <div>
                                            <div style={{fontWeight: 500}}>{selectedUser.name}</div>
                                            <Text type="secondary" style={{fontSize: "13px"}}>
                                                ID: {selectedUser.employee_id}
                                            </Text>
                                        </div>
                                    </Space>
                                </Card>
                            )}

                            {/* Todo Items Section */}
                            <div style={{
                                background: "#fafafa",
                                padding: "20px",
                                borderRadius: "8px",
                                marginBottom: "24px"
                            }}>
                                <Title level={5} style={{marginBottom: "16px"}}>
                                    <CheckCircleOutlined style={{color: "#52c41a", marginRight: "8px"}}/>
                                    Vazifalar ro'yxati
                                    {tasks.length > 0 && (
                                        <Text type="secondary" style={{fontSize: "13px", marginLeft: "8px", fontWeight: 400}}>
                                            (Drag and drop orqali tartiblang)
                                        </Text>
                                    )}
                                </Title>

                                {tasks.length === 0 ? (
                                    <Empty
                                        description="Vazifalar yo'q. Quyidagi tugma orqali vazifa qo'shing"
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        style={{padding: "20px 0"}}
                                    />
                                ) : (
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <SortableContext
                                            items={tasks.map(t => t.id.toString())}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {tasks.map((task, index) => (
                                                <SortableTaskItem
                                                    key={task.id}
                                                    id={task.id}
                                                    index={index}
                                                    task={task}
                                                    users={users}
                                                    onRemove={() => handleRemoveTask(task.id)}
                                                    onTaskChange={(updatedTask) => handleTaskChange(task.id, updatedTask)}
                                                />
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                )}

                                <Button
                                    type="dashed"
                                    onClick={handleAddTask}
                                    block
                                    icon={<PlusOutlined/>}
                                    size="middle"
                                    style={{
                                        borderRadius: "8px",
                                        borderColor: "#1890ff",
                                        color: "#1890ff",
                                        marginTop: tasks.length > 0 ? "16px" : "0"
                                    }}
                                >
                                    Yangi vazifa qo'shish
                                </Button>
                            </div>

                            {/* Action Buttons */}
                            <Form.Item style={{marginBottom: 0}}>
                                <Space size="middle">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        size="middle"
                                        icon={<SaveOutlined/>}

                                    >
                                        Saqlash
                                    </Button>
                                    <Button
                                        size="middle"
                                        onClick={() => form.resetFields()}
                                        icon={<CloseCircleOutlined/>}

                                    >
                                        Tozalash
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                {/* Right Column - Calendar & Lists */}
                <Col xs={24} lg={12}>
                    {/* Calendar */}
                    <Card
                        variant="borderless"
                        style={{
                            borderRadius: "12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                            marginBottom: "24px"
                        }}
                    >
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                            <Title level={5} style={{margin: 0}}>
                                <CalendarOutlined style={{marginRight: "8px", color: "#1890ff"}}/>
                                Vazifalar taqvimi
                            </Title>
                            <Button
                                icon={<ReloadOutlined/>}
                                onClick={fetchTodoLists}
                                loading={loadingLists}
                                size="small"
                            >
                                Yangilash
                            </Button>
                        </div>

                        <Calendar
                            fullscreen={false}
                            value={filterDate}
                            onChange={(date) => setFilterDate(date)}
                            dateCellRender={(date) => {
                                const todosForDate = todoLists.filter(
                                    todo => dayjs(todo.date).format("YYYY-MM-DD") === date.format("YYYY-MM-DD")
                                );
                                if (todosForDate.length > 0) {
                                    return (
                                        <Badge
                                            count={todosForDate.length}
                                            style={{backgroundColor: '#52c41a'}}
                                        />
                                    );
                                }
                                return null;
                            }}
                        />
                    </Card>

                    {/* Todo Lists for Selected Date */}
                    <Card
                        variant="borderless"
                        style={{
                            borderRadius: "12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                        }}
                        title={
                            <Space>
                                <CalendarOutlined style={{color: "#1890ff"}}/>
                                <span>{filterDate.format("DD MMMM YYYY")} - Vazifalar</span>
                                <Tag color="blue">{todoLists.length} ta</Tag>
                            </Space>
                        }
                    >
                        <Spin spinning={loadingLists}>
                            {todoLists.length === 0 ? (
                                <Empty
                                    description="Bu sana uchun vazifalar topilmadi"
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            ) : (
                                <List
                                    dataSource={todoLists}
                                    renderItem={(todo) => (
                                        <List.Item
                                            actions={[
                                                <Tooltip title="Tahrirlash" key="edit">
                                                    <Button
                                                        type="text"
                                                        icon={<EditOutlined/>}
                                                        onClick={() => handleEditTodo(todo)}
                                                    />
                                                </Tooltip>,
                                                <Popconfirm
                                                    title="Vazifani o'chirish"
                                                    description="Rostdan ham bu vazifani o'chirmoqchimisiz?"
                                                    onConfirm={() => handleDeleteTodo(todo.id)}
                                                    okText="Ha"
                                                    cancelText="Yo'q"
                                                    key="delete"
                                                >
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<DeleteOutlined/>}
                                                    />
                                                </Popconfirm>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={
                                                    <Avatar
                                                        style={{backgroundColor: '#1890ff'}}
                                                        icon={<UserOutlined/>}
                                                    />
                                                }
                                                title={
                                                    <Space direction="vertical" size="small" style={{width: '100%'}}>
                                                        <div>
                                                            <Text strong>{todo.assigned_to_name}</Text>
                                                            <Divider type="vertical"/>
                                                            <Tag color="blue">{dayjs(todo.date).format("DD.MM.YYYY")}</Tag>
                                                        </div>
                                                        <Space size="small" wrap>
                                                            <Tag icon={<CheckCircleOutlined/>} color="success">
                                                                {todo.completed_items}/{todo.total_items} bajarildi
                                                            </Tag>
                                                            <Tag color="processing">
                                                                {Math.round(todo.completion_percentage)}% tugallangan
                                                            </Tag>
                                                        </Space>
                                                    </Space>
                                                }
                                                description={
                                                    <div style={{marginTop: '8px'}}>
                                                        {todo.items && todo.items.slice(0, 3).map((item, idx) => (
                                                            <div key={idx} style={{fontSize: '12px', color: '#8c8c8c', marginBottom: '4px'}}>
                                                                • {item.title}
                                                                {item.time && (
                                                                    <Text type="secondary" style={{marginLeft: '8px', fontSize: '11px'}}>
                                                                        <FieldTimeOutlined/> {item.time}
                                                                    </Text>
                                                                )}
                                                            </div>
                                                        ))}
                                                        {todo.items && todo.items.length > 3 && (
                                                            <Text type="secondary" style={{fontSize: '12px'}}>
                                                                ... va yana {todo.items.length - 3} ta vazifa
                                                            </Text>
                                                        )}
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Spin>
                    </Card>
                </Col>
            </Row>

            {/* Edit Modal */}
            <Modal
                title={
                    <Space>
                        <EditOutlined/>
                        <span>Vazifani tahrirlash</span>
                    </Space>
                }
                open={editModalVisible}
                onOk={handleUpdateTodo}
                onCancel={() => {
                    setEditModalVisible(false);
                    setEditingTodo(null);
                }}
                okText="Saqlash"
                cancelText="Bekor qilish"
            >
                {editingTodo && (
                    <Space direction="vertical" style={{width: '100%'}} size="middle">
                        <div>
                            <Text strong>Foydalanuvchi:</Text>
                            <div style={{marginTop: '8px'}}>
                                <Avatar icon={<UserOutlined/>} style={{marginRight: '8px'}}/>
                                {editingTodo.assigned_to_name}
                            </div>
                        </div>

                        <div>
                            <Text strong>Sana:</Text>
                            <DatePicker
                                value={dayjs(editingTodo.date)}
                                onChange={(date) => setEditingTodo({...editingTodo, date: date.format('YYYY-MM-DD')})}
                                format="DD.MM.YYYY"
                                style={{width: '100%', marginTop: '8px'}}
                            />
                        </div>

                        <div>
                            <Text strong>Vazifalar ({editingTodo.total_items} ta):</Text>
                            <List
                                size="small"
                                dataSource={editingTodo.items || []}
                                renderItem={(item) => (
                                    <List.Item>
                                        <Space>
                                            <CheckCircleOutlined style={{color: item.status === 'completed' ? '#52c41a' : '#8c8c8c'}}/>
                                            <Text>{item.title}</Text>
                                            {item.time && (
                                                <Tag icon={<FieldTimeOutlined/>} color="blue">{item.time}</Tag>
                                            )}
                                        </Space>
                                    </List.Item>
                                )}
                                style={{marginTop: '8px'}}
                            />
                        </div>
                    </Space>
                )}
            </Modal>
        </Container>
    );
};

export default AdminCreateTodo;
